import {
  LayoutDashboard,
  Users,
  ScrollText,
  Building2,
  ClipboardList,
  IdCard,
  CalendarRange,
  BookOpen,
  Globe,
  KeyRound,
  Mail,
  Megaphone,
  FileText,
  Files,
  Trophy,
  type LucideIcon,
} from "lucide-react";
import {
  Sidebar as LibSidebar,
  type SidebarNavGroup,
} from "@marekl/adminer/shell";
import {
  type User,
  type SystemRole,
  hasAnyAssignmentRole,
  hasAnyGlobalRole,
} from "../../auth/queries";
import { BrandLockup } from "../../components/BrandLockup";
import { SupportContacts } from "../../components/SupportContacts";
import { mayOpenSeasonRegistration } from "../../lib/entryEligibility";
import { useMyTeams } from "../../lib/queries/teams";
import { useVisibleCustomPages } from "../../lib/queries/customPages";
import { customPagePath } from "../../lib/customPages";
import { useSeason } from "../../season";
import styles from "./Sidebar.module.css";

interface DemoNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  requiredRoles?: SystemRole[];
  requiredAnyAssignmentRoles?: SystemRole[];
  /** Hidden outside the registration window — the workspace behind it
   * refuses every write then, so the entry would be a door into a
   * read-only room. Admins keep it (they may register year-round). */
  requiresRegistrationOpen?: boolean;
  /** Hidden for a leader whose clubs are all outside ČNSMS: the soupiska
   * behind it is ČNSMS's, and that club never files one. */
  requiresCmfTeam?: boolean;
}

interface DemoNavGroup {
  label: string;
  requiredRoles?: SystemRole[];
  requiredAnyAssignmentRoles?: SystemRole[];
  items: DemoNavItem[];
}

const NAV_GROUPS: DemoNavGroup[] = [
  {
    label: "Přehled",
    items: [
      { to: "/dashboard", label: "Přehled", icon: LayoutDashboard, end: true },
    ],
  },
  {
    label: "Správa",
    requiredAnyAssignmentRoles: [
      "admin",
      "members_admin",
      "members_viewer",
      "competitions_admin",
      "competitions_viewer",
      "global_viewer",
      "team_leader",
      "competition_organizer",
      "federation_admin",
    ],
    items: [
      {
        to: "/clenska-zakladna",
        label: "Členská základna",
        icon: IdCard,
        end: true,
        requiredRoles: ["admin", "members_admin", "members_viewer", "global_viewer"],
      },
      {
        to: "/teams",
        label: "Členové týmu",
        icon: Building2,
        end: true,
        requiredAnyAssignmentRoles: ["team_leader"],
      },
      {
        // A resolver route rather than a team-scoped one: the sidebar has
        // no team id, so /registrace-do-sezony redirects to the leader's
        // team the way /souteze already does.
        to: "/registrace-do-sezony",
        label: "Registrace do sezóny",
        icon: ClipboardList,
        requiredAnyAssignmentRoles: ["team_leader"],
        requiresRegistrationOpen: true,
        requiresCmfTeam: true,
      },
      {
        to: "/souteze",
        label: "Soutěže a přihlášky",
        icon: Trophy,
        end: true,
        requiredAnyAssignmentRoles: ["team_leader"],
      },
      {
        to: "/admin/tymy",
        label: "Týmy",
        icon: Building2,
        // A Správce federace administers its own federation's teams, and
        // that role is federation-scoped rather than global.
        requiredAnyAssignmentRoles: [
          "admin",
          "members_admin",
          "members_viewer",
          "global_viewer",
          "federation_admin",
        ],
      },
      {
        to: "/admin/souteze",
        label: "Soutěže",
        icon: Trophy,
        requiredAnyAssignmentRoles: [
          "admin",
          "competitions_admin",
          "competitions_viewer",
          "global_viewer",
          "competition_organizer",
        ],
      },
      {
        to: "/admin/pravidla",
        label: "Pravidla",
        icon: BookOpen,
        requiredRoles: ["admin", "competitions_admin", "competitions_viewer", "global_viewer"],
      },
      {
        to: "/audit-log",
        label: "Audit log",
        icon: ScrollText,
        requiredRoles: ["admin", "members_admin", "global_viewer"],
      },
    ],
  },
  {
    label: "Administrace",
    // Every item below names its own roles, so widening the group lets the
    // správce stránek in to „Stránky" and the správce oznámení to
    // „Oznámení", and neither to anything else.
    requiredRoles: ["admin", "members_admin", "custom_pages_manager", "notices_manager", "global_viewer"],
    items: [
      { to: "/users", label: "Uživatelé", icon: Users, requiredRoles: ["admin", "global_viewer"] },
      {
        to: "/admin/sezony",
        label: "Sezóny",
        icon: CalendarRange,
        requiredRoles: ["admin", "members_admin", "global_viewer"],
      },
      { to: "/admin/federace", label: "Federace", icon: Globe, requiredRoles: ["admin", "global_viewer"] },
      { to: "/admin/maily", label: "E-maily", icon: Mail, requiredRoles: ["admin", "global_viewer"] },
      { to: "/admin/pristup", label: "Přístup", icon: KeyRound, requiredRoles: ["admin", "global_viewer"] },
      {
        to: "/admin/oznameni",
        label: "Oznámení",
        icon: Megaphone,
        requiredRoles: ["admin", "notices_manager", "global_viewer"],
      },
      {
        to: "/admin/stranky",
        label: "Stránky",
        icon: Files,
        requiredRoles: ["admin", "custom_pages_manager", "global_viewer"],
      },
    ],
  },
];

interface SidebarProps {
  // Still needed — the nav is filtered by role. The ACCOUNT moved to the
  // topbar (AppTopbar's AccountMenu); the rail no longer carries identity.
  user: User;
}

export function Sidebar({ user }: SidebarProps) {
  // Same rule as TeamDetailPage's registration link: writes only ever
  // target the active season, and outside the window only a membership
  // admin may make them.
  const { activeSeason } = useSeason();
  const showRegistration = mayOpenSeasonRegistration(activeSeason, user);
  // A leader of clubs that are all outside ČNSMS is offered no soupiska:
  // the workspace refuses them (ErrSeasonRegistrationNotForTeam). The
  // query is the one RegistraceRoute already makes, so the rail costs
  // nothing extra. While it loads the answer is „yes": the common case is
  // a Czech leader, who would otherwise watch the item appear a moment
  // late on every page; a foreign leader sees it for that one render.
  const { data: myTeams } = useMyTeams();
  const hasCmfTeam = myTeams === undefined || myTeams.some((t) => t.federation_is_primary);

  // The reader's stránky, already filtered by the server, as their own
  // group between „Správa" and „Administrace": after the work, before the
  // admin pages. No pages, no group.
  const { data: customPages = [] } = useVisibleCustomPages();
  const pagesGroup: DemoNavGroup = {
    label: "Informace",
    items: customPages.map((p) => ({ to: customPagePath(p.slug), label: p.title, icon: FileText })),
  };
  const adminAt = NAV_GROUPS.findIndex((g) => g.label === "Administrace");
  const navGroups = [...NAV_GROUPS.slice(0, adminAt), pagesGroup, ...NAV_GROUPS.slice(adminAt)];

  const groups: SidebarNavGroup[] = navGroups.map((g) => ({
    ...g,
    items: g.items.filter(
      (item) =>
        (!item.requiredRoles || hasAnyGlobalRole(user, ...item.requiredRoles)) &&
        (!item.requiredAnyAssignmentRoles || hasAnyAssignmentRole(user, ...item.requiredAnyAssignmentRoles)) &&
        (!item.requiresRegistrationOpen || showRegistration) &&
        (!item.requiresCmfTeam || hasCmfTeam),
    ),
  })).filter(
    (g) =>
      g.items.length > 0 &&
      (!g.requiredRoles || hasAnyGlobalRole(user, ...g.requiredRoles)) &&
      (!g.requiredAnyAssignmentRoles || hasAnyAssignmentRole(user, ...g.requiredAnyAssignmentRoles)),
  ).map((g) => ({
    label: g.label,
    items: g.items.map(({ to, label, icon: Icon, end }) => ({
      to,
      label,
      end,
      icon: <Icon aria-hidden="true" />,
    })),
  }));

  return (
    <LibSidebar
      groups={groups}
      // No `user` and no `onLogout`: the account lives in the topbar now.
      // The footer carries what belongs at the foot of a navigation rail
      // rather than beside a name: where to write, and the copyright line.
      footer={
        <>
          <SupportContacts tone="sidebar" />
          <span className={styles.copyright}>© ČNSMS {new Date().getFullYear()}</span>
        </>
      }
      brand={<BrandLockup compact />}
    />
  );
}
